import { createContext, useMemo, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { GET_PRICING_OVERRIDES_OPTIONS } from '@/graphql/queries/integration_meta_types';

import {
    GET_ACTIVE_INTEGRATION_OVERRIDES,
    GET_SHIPPERS,
    GET_TARIFFS,
    SET_PRICING_OPTIONS,
    GET_TARIFF_PERMISSIONS,
} from './graphql';
import { COLUMNS } from './columns';
import { useMergedOptions } from './hooks';

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const [shipper, setShipper] = useState(null);
    const [overrides, setOptionOverrides] = useState({});
    const [selectedOverride, setSelectedOptions] = useState(null);

    const reset = () => {
        setOptionOverrides({});
        setSelectedOptions(null);
    };

    const [getActive, { data: activeResults, loading: initActiveInflight, refetch: refetchIntegrationOverrides }] =
        useLazyQuery(GET_ACTIVE_INTEGRATION_OVERRIDES, {
            onError: (err) => {
                console.error(err);
                Sentry.captureException(err);
            },
        });

    const [save, { loading: savingInflight }] = useMutation(SET_PRICING_OPTIONS, {
        onCompleted: () => {
            refetchIntegrationOverrides();
        },
    });
    const { data: tariffResults, loading: initTariffsInflight } = useQuery(GET_TARIFFS);
    const { data: shipperResults, loading: initShippersInflight } = useQuery(GET_SHIPPERS);
    const { data: optionResults, loading: initOptionsInflight } = useQuery(GET_PRICING_OVERRIDES_OPTIONS);
    const { data: tariffPermissionsResults, loading: initTariffPermissionsInflight } = useQuery(GET_TARIFF_PERMISSIONS);

    useEffect(() => {
        if (shipper) {
            reset();

            getActive({
                variables: {
                    client_id: shipper,
                },
            });
        }
    }, [shipper]);

    const shipperOptions = useMemo(() => {
        const shippers = shipperResults?.results || [];

        return shippers.map((client) => ({
            label: client.business_name,
            value: client.client_id,
        }));
    }, [shipperResults]);

    const canSeeOption = useCallback(
        ({ option, tariff }) => {
            const map = (tariffPermissionsResults?.results || []).reduce((acc, permission) => {
                acc[permission.client_id] = {
                    ...(acc[permission.client_id] || {}),
                    [permission.entity_id]: true,
                };

                return acc;
            }, {});

            const hasExplicitPermission = map[shipper]?.[tariff.client_id];
            const localTariff = tariff.client_id === shipper;
            const carrierTariff = option.pricing_overrides?.[0]?.assign_id === tariff.client_id;

            return hasExplicitPermission || localTariff || carrierTariff;
        },
        [tariffPermissionsResults, shipper]
    );

    const { pricingOptions, optionsMap, marketplaceOption, merged } = useMergedOptions({
        tariffResults,
        activeResults,
        optionResults,
        shipper,
        overrides,
        canSeeOption,
    });

    const selected = useMemo(() => {
        return selectedOverride || Object.keys(merged);
    }, [merged, selectedOverride]);

    const selectedObj = useMemo(() => {
        return selected.map((id) => optionsMap[id]).filter((option) => !!option);
    }, [selected, optionsMap]);

    const rows = useMemo(() => {
        if (!shipper) {
            return [];
        }

        return [
            marketplaceOption,
            ...Object.values(
                selected.reduce((acc, name) => {
                    return {
                        ...acc,
                        [name]: {
                            ...(optionsMap[name] || {}),
                            ...(merged[name] || {}),
                            ...(overrides[name] || {}),
                        },
                    };
                }, {})
            ),
        ];
    }, [merged, overrides, optionsMap, selected, marketplaceOption, shipper]);

    const isValid = useMemo(() => {
        const hasMarketplaceTariff = !!marketplaceOption.tariff_id;
        const hasRequiredCarrierTariffs = rows.every((row) => {
            const missingCarrierTariff = row.requires_carrier_tariff && !row.carrier_tariff_id;

            return !missingCarrierTariff;
        });

        return hasMarketplaceTariff && hasRequiredCarrierTariffs;
    }, [marketplaceOption, rows]);

    const serialized = useMemo(() => {
        const ATTRS = [
            'name',
            'tariff_id',
            'add_margin_flat',
            'add_margin_rate',
            'zip_geofence',
            'marketplace_tier',
            'carrier_tariff_id',
        ];

        return `{${rows
            .filter((row) => row.name !== 'Marketplace')
            .map((row) => {
                return `"(${ATTRS.map((attr) => {
                    const value = row[attr] ?? '';

                    return attr === 'name' ? `\\"${value}\\"` : value;
                }).join(',')})"`;
            })
            .join(', ')}}`;
    }, [rows]);

    const callbacks = {
        selectShipper: (id) => {
            setShipper(id);
        },
        selectOption: (values) => {
            setSelectedOptions(values);
        },
        setOverride: ({ name, attr, value }) => {
            setOptionOverrides((prev) => {
                return {
                    ...prev,
                    [name]: {
                        ...prev[name],
                        [attr]: value,
                    },
                };
            });
        },
        save: () => {
            save({
                variables: {
                    client_id: shipper,
                    options: serialized,
                    marketplace_tariff_id: marketplaceOption.tariff_id,
                    marketplace_margin_flat: marketplaceOption.add_margin_flat,
                    marketplace_margin_rate: marketplaceOption.add_margin_rate,
                },
            });
        },
    };

    return (
        <Context.Provider
            value={{
                state: {
                    shipperOptions,
                    shipper,
                    selected,
                    pricingOptions,
                    overrides,
                    selectedObj,
                    hasMore: false,
                    columns: COLUMNS,
                    rows,
                    isValid,
                    height: 128,
                },
                loading: {
                    init:
                        initShippersInflight ||
                        initOptionsInflight ||
                        initActiveInflight ||
                        initTariffsInflight ||
                        initTariffPermissionsInflight,
                    save: initActiveInflight || savingInflight,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
